// Single source of truth for the AI-facing ExperienceJSON reference.
// Edits ship via normal commit/deploy. Manually keep in sync with
// website/src/types/experience.ts — there is no build-time generation.
// schema-checked: 2026-05-18
export const AI_DOCS = `# Album Cards Experience JSON — AI Reference

## 1. What this is

You produce or edit a JSON blob (\`ExperienceJSON\`) that renders an in-app music experience layered over an audio player. Input: the artist's natural-language request and, optionally, an existing \`ExperienceJSON\`. Output: the complete updated \`ExperienceJSON\`. The artist will paste your output verbatim back into the builder, so it must be valid, parseable JSON that conforms to the schema in section 4.

## 2. Mental model

The experience is a set of named **layouts** (screens). One layout is named \`default\` and is shown first; other layouts activate via triggers (a track starting, a playback time reached, or a button navigation). Each layout has a **canvas** (defaults to 375×875, mobile portrait) and an ordered list of **elements**. Origin is top-left at \`(0, 0)\`; \`x\` increases rightward, \`y\` increases downward. Positioning is absolute — every element specifies its own \`x\`, \`y\`, \`width\`, and \`height\` in canvas units. Elements later in the \`elements\` array render **on top of** earlier ones (paint order). The runtime drives the playback clock and exposes the currently-playing track ID; you react to those signals via **layout triggers** (whole-screen swaps), **per-element keyframes** keyed by trackId (time-based animation within a track), **states** (conditional overrides), and **animations** (older property-loop system, useful for continuous loops like a spinning record).

## 3. Output rules

- Output **pure JSON only**. No comments, no Markdown fences, no surrounding prose. The entire response must be a single JSON object parseable by \`JSON.parse\`.
- **Never invent fields.** Use only the fields defined in section 4. Unknown fields will be silently ignored or cause render errors.
- When editing an existing JSON, **preserve fields you aren't changing** — including element \`id\`s, unrelated layouts, variables, and styles.
- **Migrate deprecated fields.** If you encounter any of these in input, convert them to current-schema equivalents and do **not** emit them in your output:
  - \`textKeyframes\` on an element → merge into per-element \`keyframes\` (keyed by trackId) or into \`states\`.
  - \`visibilityTriggers\` on an element → express as a \`states\` entry with a variable condition that overrides \`style.visible\`.
  - Legacy single-action button fields (\`action\`, \`targetLayout\`, \`targetVariable\`, \`variableValue\`) → replace with the \`actions\` array using discriminated \`ButtonAction\` objects.
  - Top-level \`timelineKeyframes\` → distribute into per-element \`keyframes\`.
- **Ask the artist for image and video URLs** rather than inventing them. Reuse URLs already present in the input JSON when possible. If a placeholder is unavoidable, use a clearly-marked URL like \`https://placehold.co/320x320/cccccc/000000?text=REPLACE\` and call it out so the artist can swap it.
- **Element \`id\`s must be unique across the entire experience.** Use kebab-case, short, descriptive (\`album-art\`, \`track-title\`, \`lyrics-btn\`).
- Numbers are unitless canvas pixels unless otherwise noted. Times are in **seconds** unless otherwise noted.

## 4. Schema reference

### 4.1 \`ExperienceJSON\` (top level)

| field        | type                                              | required | notes                                                  |
|--------------|---------------------------------------------------|----------|--------------------------------------------------------|
| \`version\`    | \`string\`                                          | yes      | Use \`"1.0"\`.                                          |
| \`layouts\`    | \`{ default: Layout, [name: string]: Layout }\`     | yes      | Must contain a \`default\` key. Others are addressable. |
| \`variables\`  | \`VariableDefinition[]\`                            | no       | Runtime-mutable string variables.                      |

### 4.2 \`Canvas\`

| field             | type     | required | default     | notes                            |
|-------------------|----------|----------|-------------|----------------------------------|
| \`width\`           | \`number\` | yes      | 375         | Canvas units.                    |
| \`height\`          | \`number\` | yes      | 875         | Canvas units.                    |
| \`backgroundColor\` | \`string\` | no       | (none)      | Any CSS color, e.g. \`"#0a0a0a"\`. |

### 4.3 Elements — common shape and types

Every element has these common fields:

| field         | type                                                       | required | notes                                                            |
|---------------|------------------------------------------------------------|----------|------------------------------------------------------------------|
| \`id\`          | \`string\`                                                   | yes      | Unique across the entire experience.                             |
| \`type\`        | \`'image' \\| 'view' \\| 'text' \\| 'video' \\| 'button'\` | yes      | Discriminates the element variant.                               |
| \`style\`       | \`ElementStyle\`                                             | no       | See 4.4.                                                         |
| \`keyframes\`   | \`{ [trackId: number]: ElementKeyframe[] }\`                 | no       | Per-track time-based overrides. See 4.5.                         |
| \`animations\`  | \`Animation[]\`                                              | no       | Property-loop animations. See 4.9.                               |
| \`states\`      | \`ElementState[]\`                                           | no       | Conditional overrides (variables, trackStart). See 4.6.          |

Type-specific extra fields:

- **\`image\`**: \`src: string\` (URL, required).
- **\`text\`**: \`text: string\` (default/initial text, required).
- **\`view\`**: \`children?: ExperienceElement[]\` (nested elements; the view acts as a positioned container).
- **\`video\`**: \`src: string\` (required), \`muted?: boolean\`, \`loop?: boolean\`.
- **\`button\`**: \`text: string\` (required, the button label), \`actions?: ButtonAction[]\` (executed in array order on click).

### 4.4 \`ElementStyle\`

All fields are optional. Numbers are unitless canvas pixels unless noted.

| field            | type                                                                  | meaning                                                              |
|------------------|-----------------------------------------------------------------------|----------------------------------------------------------------------|
| \`x\`              | \`number\`                                                              | Left position in canvas units (top-left origin).                     |
| \`y\`              | \`number\`                                                              | Top position in canvas units.                                        |
| \`width\`          | \`number\`                                                              | Element width in canvas units.                                       |
| \`height\`         | \`number\`                                                              | Element height in canvas units.                                      |
| \`opacity\`        | \`number\` (0–1)                                                        | 0 invisible, 1 fully opaque.                                         |
| \`scale\`          | \`number\`                                                              | 1 = no scaling. 0.5 = half size. 2 = double.                         |
| \`rotation\`       | \`number\`                                                              | Degrees clockwise.                                                   |
| \`translateX\`     | \`number\`                                                              | Post-positioning offset on x-axis (canvas units). Great for keyframes.|
| \`translateY\`     | \`number\`                                                              | Post-positioning offset on y-axis (canvas units).                    |
| \`backgroundColor\`| \`string\`                                                              | Any CSS color (also supports gradients, e.g. \`linear-gradient(...)\`). |
| \`borderRadius\`   | \`number\`                                                              | Pixels.                                                              |
| \`color\`          | \`string\`                                                              | Text color (CSS color).                                              |
| \`fontSize\`       | \`number\`                                                              | Pixels.                                                              |
| \`fontFamily\`     | \`FontFamily\` (see 4.11)                                               | Must be one of the allowed font names.                               |
| \`fontWeight\`     | \`'normal' \\| 'bold' \\| '100' \\| '200' \\| '300' \\| '400' \\| '500' \\| '600' \\| '700' \\| '800' \\| '900'\` | Weight as a string.                                                  |
| \`textAlign\`      | \`'left' \\| 'center' \\| 'right' \\| 'justify'\`                       | Text alignment within the element.                                   |
| \`lineHeight\`     | \`number\`                                                              | Unitless multiplier (e.g. 1.2).                                      |
| \`padding\`        | \`number\`                                                              | Pixels, applied uniformly on all sides.                              |
| \`marquee\`        | \`boolean\`                                                             | Text only. If true, text scrolls horizontally.                       |
| \`visible\`        | \`boolean\`                                                             | If false, the element is hidden. Default: true.                      |

### 4.5 Per-element \`keyframes\`

\`keyframes\` is a map from trackId (number) to an ordered array of \`ElementKeyframe\`. The runtime activates the keyframe array for the **currently-playing track** and animates the element relative to the elapsed time within that track.

\`ElementKeyframe\`:

| field         | type                       | required | notes                                                                              |
|---------------|----------------------------|----------|------------------------------------------------------------------------------------|
| \`time\`        | \`number\`                   | yes      | Seconds into the track.                                                            |
| \`style\`       | \`Partial<ElementStyle>\`    | no       | Sparse — list **only** the properties changing at this keyframe.                   |
| \`text\`        | \`string\`                   | no       | Text-element override (replaces \`element.text\` at this time).                      |
| \`interpolate\` | \`boolean\`                  | no       | If true, the runtime tweens style numerics from the **previous keyframe** to this one. Defaults to a hard cut at \`time\`. |
| \`easing\`      | \`EasingFunction\`           | no       | Easing curve applied when \`interpolate\` is true. Default \`linear\`.                 |

Notes:
- Keyframes drive numeric styles only (opacity, translateX, scale, etc.) and text. They do **not** swap an element's \`src\` — use \`states\` or separate elements toggled via \`style.visible\` for per-track image/video swaps.
- The first keyframe at \`time: 0\` defines the starting state for that track. Subsequent keyframes with \`interpolate: true\` animate to their styles over \`time - previous.time\` seconds.

### 4.6 \`ElementState\`

A state is a set of overrides that apply when **all** of its \`conditions\` are satisfied (AND logic). If multiple states are active, later array entries win.

| field        | type                       | required | notes                                                  |
|--------------|----------------------------|----------|--------------------------------------------------------|
| \`id\`         | \`string\`                   | yes      | Unique within the element.                             |
| \`conditions\` | \`StateCondition[]\`         | yes      | All must hold for the state to be active.              |
| \`text\`       | \`string\`                   | no       | Text-element override.                                 |
| \`style\`      | \`Partial<ElementStyle>\`    | no       | Sparse style overrides (including \`visible\`).          |

\`StateCondition\`:

| field      | type                                | required | notes                                                              |
|------------|-------------------------------------|----------|--------------------------------------------------------------------|
| \`type\`     | \`'variable' \\| 'trackStart'\`     | no       | Default \`'variable'\`.                                              |
| \`variable\` | \`string\`                           | when \`type\` is \`variable\` | Variable name.                                            |
| \`value\`    | \`string\`                           | when \`type\` is \`variable\` | Required string value for the condition to hold.          |
| \`trackId\`  | \`number\`                           | when \`type\` is \`trackStart\` | Active once this track has started playing.             |

States cannot override \`src\` on image or video elements. To swap art per track, use either: (a) two separate image elements toggled via \`style.visible\` in states, or (b) put each track on its own layout via a \`trackStart\` \`LayoutTrigger\`.

### 4.7 \`VariableDefinition\`

| field          | type     | required | notes                                                  |
|----------------|----------|----------|--------------------------------------------------------|
| \`name\`         | \`string\` | yes      | Identifier referenced by states and \`setVariable\` actions. |
| \`defaultValue\` | \`string\` | yes      | All variables are strings (use \`"true"\`/\`"false"\` for booleans). |

Variables are runtime-mutable via the \`setVariable\` button action (see 4.10).

### 4.8 \`LayoutTrigger\`

Specifies when a non-\`default\` layout activates. The \`default\` layout must **not** have a trigger.

| field      | type                                      | required | notes                                            |
|------------|-------------------------------------------|----------|--------------------------------------------------|
| \`event\`    | \`'trackStart' \\| 'playbackPosition'\`   | yes      | What kind of event activates the layout.         |
| \`trackId\`  | \`number\`                                  | when \`event\` is \`trackStart\` | Which track must start.                |
| \`time\`     | \`number\`                                  | when \`event\` is \`playbackPosition\` | Time in seconds from playback start. |

A layout activated only via button navigation (\`navigateToLayout\`) should **omit** \`trigger\` entirely.

### 4.9 \`Animation\` (property-loop)

The older, simpler system for continuous property animations on a single element. Prefer per-element \`keyframes\` (4.5) for narrative time-based motion; use \`animations\` for repeating loops (e.g., a constantly rotating album art).

\`Animation\`:

| field      | type                                                                | required | notes                                                                  |
|------------|---------------------------------------------------------------------|----------|------------------------------------------------------------------------|
| \`property\` | \`'opacity' \\| 'scale' \\| 'rotation' \\| 'translateX' \\| 'translateY'\` | yes      | Single property to animate.                                            |
| \`trigger\`  | \`{ event: 'playbackPosition', time: number }\`                       | no       | Start time in seconds (absolute playback position). If omitted, starts at 0. |
| \`keyframes\`| \`Keyframe[]\`                                                        | yes      | Ordered keyframes.                                                     |
| \`loop\`     | \`boolean\`                                                           | no       | If true, the animation restarts after the last keyframe.               |

\`Keyframe\`:

| field    | type                | required | notes                                                                |
|----------|---------------------|----------|----------------------------------------------------------------------|
| \`at\`     | \`number\`            | yes      | Time **in seconds** within the animation's local timeline (i.e. elapsed since \`trigger.time\`). First keyframe is typically \`at: 0\`. |
| \`value\`  | \`number\`            | yes      | Property value at this keyframe.                                     |
| \`easing\` | \`EasingFunction\`    | no       | Easing curve from previous keyframe to this one. Default \`linear\`.   |

### 4.10 \`ButtonAction\` (discriminated union)

\`\`\`
{ "type": "navigateToLayout", "targetLayout": "lyrics" }
{ "type": "setVariable",      "variableName": "theme", "value": "dark" }
\`\`\`

A button may have multiple actions in its \`actions\` array; they execute in order on click. For example, \`setVariable\` then \`navigateToLayout\` to mutate state and immediately swap screens.

### 4.11 Enums

- **\`EasingFunction\`**: \`linear\` | \`easeIn\` | \`easeOut\` | \`easeInOut\`
- **\`FontFamily\`** (must match exactly, case-sensitive):
  \`Host Grotesk\` | \`ChicagoFLF\` | \`IBM Plex Mono\` | \`Instrument Serif\` | \`Inter\` | \`Michroma\` | \`Oswald\` | \`Playfair Display\` | \`Space Grotesk\` | \`VT323\` | \`Xanh Mono\`
- **\`fontWeight\`**: \`normal\` | \`bold\` | \`100\` | \`200\` | \`300\` | \`400\` | \`500\` | \`600\` | \`700\` | \`800\` | \`900\`
- **\`textAlign\`**: \`left\` | \`center\` | \`right\` | \`justify\`

## 5. Recipes

Each recipe shows the relevant fragment, not a full \`ExperienceJSON\`. Drop the fragment into the appropriate place (element entry in a layout's \`elements\` array, or top-level field).

### 5.1 Fade in an element on track 1 start

\`\`\`
{
  "id": "intro-text",
  "type": "text",
  "text": "WELCOME",
  "style": { "x": 16, "y": 400, "width": 343, "fontSize": 32, "color": "#ffffff", "opacity": 0 },
  "keyframes": {
    "1": [
      { "time": 0,   "style": { "opacity": 0 } },
      { "time": 1.5, "style": { "opacity": 1 }, "interpolate": true, "easing": "easeOut" }
    ]
  }
}
\`\`\`

### 5.2 Slide in image from offscreen-left when track 2 starts

\`\`\`
{
  "id": "track-2-art",
  "type": "image",
  "src": "https://example.com/track2.jpg",
  "style": { "x": 48, "y": 80, "width": 280, "height": 280, "translateX": -400 },
  "keyframes": {
    "2": [
      { "time": 0,   "style": { "translateX": -400 } },
      { "time": 0.8, "style": { "translateX": 0 }, "interpolate": true, "easing": "easeOut" }
    ]
  }
}
\`\`\`

### 5.3 Continuously rotating element via \`animations\`

\`\`\`
{
  "id": "spinning-record",
  "type": "image",
  "src": "https://example.com/record.png",
  "style": { "x": 87, "y": 200, "width": 200, "height": 200 },
  "animations": [
    {
      "property": "rotation",
      "keyframes": [
        { "at": 0, "value": 0 },
        { "at": 4, "value": 360, "easing": "linear" }
      ],
      "loop": true
    }
  ]
}
\`\`\`

### 5.4 Swap text per track via per-element keyframes

\`\`\`
{
  "id": "track-title",
  "type": "text",
  "text": "Track One",
  "style": { "x": 16, "y": 560, "width": 343, "fontSize": 24, "color": "#ffffff", "textAlign": "center" },
  "keyframes": {
    "1": [ { "time": 0, "text": "Track One" } ],
    "2": [ { "time": 0, "text": "Track Two" } ],
    "3": [ { "time": 0, "text": "Track Three" } ]
  }
}
\`\`\`

### 5.5 Swap image per track (states + \`visible\`, since \`src\` is not keyframable)

Use two image elements positioned identically; toggle \`visible\` based on which track has started. The fragment below shows **two entries** that go inside the same \`elements\` array — do NOT emit them as bare comma-separated objects.

\`\`\`
"elements": [
  {
    "id": "art-track-1",
    "type": "image",
    "src": "https://example.com/art1.jpg",
    "style": { "x": 48, "y": 80, "width": 280, "height": 280, "visible": true },
    "states": [
      { "id": "hide-after-1", "conditions": [ { "type": "trackStart", "trackId": 2 } ], "style": { "visible": false } }
    ]
  },
  {
    "id": "art-track-2",
    "type": "image",
    "src": "https://example.com/art2.jpg",
    "style": { "x": 48, "y": 80, "width": 280, "height": 280, "visible": false },
    "states": [
      { "id": "show-on-2", "conditions": [ { "type": "trackStart", "trackId": 2 } ], "style": { "visible": true } }
    ]
  }
]
\`\`\`

Alternatively, put each track on its own layout activated by a \`trackStart\` \`LayoutTrigger\` (see recipe 5.9).

### 5.6 Button that navigates to a "lyrics" layout

\`\`\`
{
  "id": "show-lyrics-btn",
  "type": "button",
  "text": "Show lyrics",
  "style": { "x": 100, "y": 780, "width": 175, "height": 44, "backgroundColor": "#ffffff", "color": "#000000", "borderRadius": 22, "fontSize": 14, "textAlign": "center" },
  "actions": [ { "type": "navigateToLayout", "targetLayout": "lyrics" } ]
}
\`\`\`

### 5.7 Button that sets a variable

\`\`\`
{
  "id": "dark-mode-btn",
  "type": "button",
  "text": "Dark mode",
  "style": { "x": 100, "y": 780, "width": 175, "height": 44, "backgroundColor": "#222222", "color": "#ffffff", "borderRadius": 22, "fontSize": 14, "textAlign": "center" },
  "actions": [ { "type": "setVariable", "variableName": "theme", "value": "dark" } ]
}
\`\`\`

### 5.8 Element visible only when a variable equals a value

\`\`\`
{
  "id": "dark-overlay",
  "type": "view",
  "style": { "x": 0, "y": 0, "width": 375, "height": 875, "backgroundColor": "#000000", "opacity": 0.6, "visible": false },
  "states": [
    {
      "id": "show-when-dark",
      "conditions": [ { "type": "variable", "variable": "theme", "value": "dark" } ],
      "style": { "visible": true }
    }
  ]
}
\`\`\`

### 5.9 Auto-switch entire layout when track 3 starts

\`\`\`
"track-3-screen": {
  "canvas": { "width": 375, "height": 875, "backgroundColor": "#1a0033" },
  "trigger": { "event": "trackStart", "trackId": 3 },
  "elements": [ /* ... */ ]
}
\`\`\`

### 5.10 Time-based layout switch (60 seconds in)

\`\`\`
"interlude": {
  "canvas": { "width": 375, "height": 875, "backgroundColor": "#000000" },
  "trigger": { "event": "playbackPosition", "time": 60 },
  "elements": [ /* ... */ ]
}
\`\`\`

### 5.11 Marquee scrolling text

\`\`\`
{
  "id": "ticker",
  "type": "text",
  "text": "NEW ALBUM OUT NOW — NEW ALBUM OUT NOW — NEW ALBUM OUT NOW",
  "style": { "x": 0, "y": 30, "width": 375, "fontSize": 14, "color": "#ffffff", "marquee": true }
}
\`\`\`

### 5.12 State-driven color change on track start

\`\`\`
{
  "id": "title",
  "type": "text",
  "text": "ALBUM",
  "style": { "x": 16, "y": 200, "width": 343, "fontSize": 48, "color": "#ffffff", "fontFamily": "Michroma" },
  "states": [
    {
      "id": "highlight-on-track-2",
      "conditions": [ { "type": "trackStart", "trackId": 2 } ],
      "style": { "color": "#00ff41" }
    }
  ]
}
\`\`\`

### 5.13 Grouped elements inside a \`view\`

A \`view\` acts as a positioned container. Its \`children\` are still absolutely positioned (their \`x\`/\`y\` are in canvas units, not relative to the parent — the view is purely for logical grouping and shared background/visibility control).

\`\`\`
{
  "id": "lyrics-group",
  "type": "view",
  "style": { "x": 0, "y": 0, "width": 375, "height": 875, "backgroundColor": "#000000", "opacity": 0.85 },
  "children": [
    { "id": "lyrics-line-1", "type": "text", "text": "First line of lyrics", "style": { "x": 16, "y": 200, "width": 343, "fontSize": 20, "color": "#ffffff" } },
    { "id": "lyrics-line-2", "type": "text", "text": "Second line of lyrics", "style": { "x": 16, "y": 240, "width": 343, "fontSize": 20, "color": "#ffffff" } }
  ]
}
\`\`\`

### 5.14 Background video

\`\`\`
{
  "id": "bg-video",
  "type": "video",
  "src": "https://example.com/visualizer.mp4",
  "muted": true,
  "loop": true,
  "style": { "x": 0, "y": 0, "width": 375, "height": 875, "opacity": 0.5 }
}
\`\`\`

### 5.15 Multi-action button (set variable, then navigate)

\`\`\`
{
  "id": "enter-btn",
  "type": "button",
  "text": "Enter",
  "style": { "x": 100, "y": 780, "width": 175, "height": 44, "backgroundColor": "#ffffff", "color": "#000000", "borderRadius": 22, "fontSize": 14, "textAlign": "center" },
  "actions": [
    { "type": "setVariable", "variableName": "hasEntered", "value": "true" },
    { "type": "navigateToLayout", "targetLayout": "main" }
  ]
}
\`\`\`

## 6. Full worked example

A 3-track album experience with a default layout, a lyrics layout reached via a button, swapping titles per track, a subtle rotating album-art animation, a now-playing badge driven by states, and a \`theme\` variable.

\`\`\`
{
  "version": "1.0",
  "variables": [
    { "name": "theme", "defaultValue": "default" }
  ],
  "layouts": {
    "default": {
      "canvas": { "width": 375, "height": 875, "backgroundColor": "#0d0d10" },
      "elements": [
        {
          "id": "album-art",
          "type": "image",
          "src": "https://placehold.co/280x280/1a1a22/ffffff?text=ALBUM",
          "style": { "x": 47, "y": 90, "width": 280, "height": 280, "borderRadius": 8 },
          "animations": [
            {
              "property": "rotation",
              "keyframes": [
                { "at": 0,  "value": 0 },
                { "at": 24, "value": 360, "easing": "linear" }
              ],
              "loop": true
            }
          ]
        },
        {
          "id": "now-playing",
          "type": "text",
          "text": "NOW PLAYING",
          "style": { "x": 16, "y": 410, "width": 343, "fontSize": 11, "fontFamily": "IBM Plex Mono", "color": "#888888", "textAlign": "center", "fontWeight": "500" },
          "states": [
            { "id": "t1", "conditions": [ { "type": "trackStart", "trackId": 1 } ], "text": "TRACK 01 / 03" },
            { "id": "t2", "conditions": [ { "type": "trackStart", "trackId": 2 } ], "text": "TRACK 02 / 03" },
            { "id": "t3", "conditions": [ { "type": "trackStart", "trackId": 3 } ], "text": "TRACK 03 / 03" }
          ]
        },
        {
          "id": "track-title",
          "type": "text",
          "text": "First Light",
          "style": { "x": 16, "y": 440, "width": 343, "fontSize": 28, "fontFamily": "Instrument Serif", "color": "#ffffff", "textAlign": "center", "lineHeight": 1.1 },
          "keyframes": {
            "1": [ { "time": 0, "text": "First Light" } ],
            "2": [ { "time": 0, "text": "Half Past Memory" } ],
            "3": [ { "time": 0, "text": "Dawn Theory" } ]
          }
        },
        {
          "id": "track-subtitle",
          "type": "text",
          "text": "Side A",
          "style": { "x": 16, "y": 490, "width": 343, "fontSize": 13, "fontFamily": "Host Grotesk", "color": "#aaaaaa", "textAlign": "center", "opacity": 0 },
          "keyframes": {
            "1": [
              { "time": 0,   "style": { "opacity": 0 } },
              { "time": 1.2, "style": { "opacity": 1 }, "interpolate": true, "easing": "easeOut" }
            ]
          }
        },
        {
          "id": "dark-veil",
          "type": "view",
          "style": { "x": 0, "y": 0, "width": 375, "height": 875, "backgroundColor": "#000000", "opacity": 0.35, "visible": false },
          "states": [
            { "id": "dark", "conditions": [ { "type": "variable", "variable": "theme", "value": "dark" } ], "style": { "visible": true } }
          ]
        },
        {
          "id": "lyrics-btn",
          "type": "button",
          "text": "Show lyrics",
          "style": { "x": 100, "y": 770, "width": 175, "height": 44, "backgroundColor": "#ffffff", "color": "#0d0d10", "borderRadius": 22, "fontSize": 14, "fontFamily": "Host Grotesk", "fontWeight": "600", "textAlign": "center" },
          "actions": [ { "type": "navigateToLayout", "targetLayout": "lyrics" } ]
        },
        {
          "id": "theme-btn",
          "type": "button",
          "text": "Dark",
          "style": { "x": 285, "y": 30, "width": 74, "height": 28, "backgroundColor": "#222228", "color": "#ffffff", "borderRadius": 14, "fontSize": 11, "fontFamily": "Host Grotesk", "textAlign": "center" },
          "actions": [ { "type": "setVariable", "variableName": "theme", "value": "dark" } ]
        }
      ]
    },
    "lyrics": {
      "canvas": { "width": 375, "height": 875, "backgroundColor": "#000000" },
      "elements": [
        {
          "id": "lyrics-title",
          "type": "text",
          "text": "Lyrics",
          "style": { "x": 16, "y": 60, "width": 343, "fontSize": 13, "fontFamily": "IBM Plex Mono", "color": "#888888", "textAlign": "center" }
        },
        {
          "id": "lyrics-body",
          "type": "text",
          "text": "The morning broke in halves\\nAnd I was holding both",
          "style": { "x": 24, "y": 200, "width": 327, "fontSize": 22, "fontFamily": "Instrument Serif", "color": "#ffffff", "textAlign": "center", "lineHeight": 1.4 },
          "keyframes": {
            "1": [ { "time": 0, "text": "The morning broke in halves\\nAnd I was holding both" } ],
            "2": [ { "time": 0, "text": "Half past memory\\nI lost track of the hour" } ],
            "3": [ { "time": 0, "text": "Dawn theory: that the light\\nis always already arriving" } ]
          }
        },
        {
          "id": "back-btn",
          "type": "button",
          "text": "Back",
          "style": { "x": 100, "y": 770, "width": 175, "height": 44, "backgroundColor": "#ffffff", "color": "#000000", "borderRadius": 22, "fontSize": 14, "fontFamily": "Host Grotesk", "fontWeight": "600", "textAlign": "center" },
          "actions": [ { "type": "navigateToLayout", "targetLayout": "default" } ]
        }
      ]
    }
  }
}
\`\`\`
`;
